// Derive a candidate colour palette from an image — CLIENT-SIDE ONLY (REQ-THEME-004). The image
// is never sent to a cloud model; a member picks a swatch, which then runs through the AA-safe
// accent corrector before it can apply. derivePalette is pure (RGBA in, hex[] out) via median-cut;
// extractPaletteFromImage is the thin canvas glue that downscales an image and calls it.

type RGB = [number, number, number];

const toHex = (v: number) => Math.round(v).toString(16).padStart(2, "0");
const rgbToHex = ([r, g, b]: RGB) => `#${toHex(r)}${toHex(g)}${toHex(b)}`;

function channelRange(box: RGB[], ch: 0 | 1 | 2): number {
  let min = 255;
  let max = 0;
  for (const p of box) {
    if (p[ch] < min) min = p[ch];
    if (p[ch] > max) max = p[ch];
  }
  return max - min;
}
const boxRange = (box: RGB[]): number =>
  Math.max(channelRange(box, 0), channelRange(box, 1), channelRange(box, 2));
function longestChannel(box: RGB[]): 0 | 1 | 2 {
  const r = channelRange(box, 0);
  const g = channelRange(box, 1);
  const b = channelRange(box, 2);
  if (r >= g && r >= b) return 0;
  return g >= b ? 1 : 2;
}
function average(box: RGB[]): RGB {
  let r = 0;
  let g = 0;
  let b = 0;
  for (const p of box) {
    r += p[0];
    g += p[1];
    b += p[2];
  }
  const n = box.length;
  return [r / n, g / n, b / n];
}

/** Median-cut palette from RGBA pixel data. Returns up to `count` hex colours, dominant first. */
export function derivePalette(rgba: Uint8ClampedArray | number[], count = 5): string[] {
  const px: RGB[] = [];
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    if (rgba[i + 3] >= 128) px.push([rgba[i], rgba[i + 1], rgba[i + 2]]);
  }
  if (px.length === 0) return [];

  let boxes: RGB[][] = [px];
  while (boxes.length < count && boxes.some((b) => b.length > 1)) {
    boxes.sort((a, b) => boxRange(b) - boxRange(a));
    const box = boxes.shift()!;
    if (box.length < 2) {
      boxes.push(box);
      break;
    }
    const ch = longestChannel(box);
    const sorted = [...box].sort((a, b) => a[ch] - b[ch]);
    const mid = sorted.length >> 1;
    boxes.push(sorted.slice(0, mid), sorted.slice(mid));
  }

  return boxes
    .filter((b) => b.length > 0)
    .sort((a, b) => b.length - a.length || rgbToHex(average(a)).localeCompare(rgbToHex(average(b))))
    .slice(0, count)
    .map((b) => rgbToHex(average(b)));
}

/** Downscale an image (or bitmap) to a small canvas and derive a palette (browser glue). The
 *  source is a local ImageBitmap from the uploaded file, so the pixels never leave the browser. */
export function extractPaletteFromImage(src: CanvasImageSource, count = 5, size = 48): string[] {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const cx = canvas.getContext("2d");
  if (!cx) return [];
  cx.drawImage(src, 0, 0, size, size);
  const { data } = cx.getImageData(0, 0, size, size);
  return derivePalette(data, count);
}
